import { API_URL, HASH_API_TOKEN } from "config";
import { managedFetch } from "utils/FetchManager";
import { fetchIntermixGeoJSON, withArrowAccept } from "utils/intermixArrow";
import { useEffect } from "react";
import { useRuns } from "contexts/RunsContext";
import usePage from "hooks/usePage";
import usePrevious from "hooks/usePrevious";
import { useAuth } from "contexts/AuthContext";

export default function UrbanSimulationDetails() {
  const { runId, isSharePage } = usePage();
  const {
    setIntermixResults,
    intermixRunID,
    setIntermixRunID,
    setAnnotations,
    setIntermixScenarioTree,
    setIntermixScenarioEmbers,
    setIntermixScenarioSequence,
    setIsLoadingIntermixSummary,
    setIsLoadingIntermixExtended,
    selectedIntermixRunIndex,
    setSelectedIntermixRunIndex,
    setIntermixRunMetadata,
    setIntermixRunsList,
  } = useRuns();

  const previousRunId = usePrevious(runId);
  const { currentUser } = useAuth();
  const BEARER_TOKEN = isSharePage ? HASH_API_TOKEN : currentUser?.accessToken;
  const prefix = isSharePage ? "/hash" : "";
  const filterQuery = isSharePage
    ? `filter_simulation_hash=${runId}`
    : `filter_simulation_id=${runId}`;

  useEffect(() => {
    if (runId === previousRunId) {
      return;
    }

    setIsLoadingIntermixSummary(true);
    setIsLoadingIntermixExtended(true);

    // Clear old scenario data and reset run index when switching runs
    setIntermixScenarioTree(null);
    setIntermixScenarioEmbers(null);
    setIntermixScenarioSequence(null);
    setIntermixRunsList(null);
    setSelectedIntermixRunIndex(1);

    if (!runId) {
      setIntermixRunMetadata(null);
      setIsLoadingIntermixSummary(false);
      setIsLoadingIntermixExtended(false);
      return;
    }
    const authHeaders = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${BEARER_TOKEN}`,
    };
    const fetchSimulationData = async () => {
      try {
        // Composite intermix result is the largest payload; ask for Arrow.
        const resultPromise = fetchIntermixGeoJSON(
          managedFetch(
            `${API_URL}${prefix}/simulation/intermix/${runId}`,
            withArrowAccept({ headers: authHeaders }),
            isSharePage ? null : currentUser
          )
        );
        const detailsPromise = managedFetch(
          `${API_URL}${prefix}/simulation?${filterQuery}`,
          { headers: authHeaders },
          isSharePage ? null : currentUser
        );
        const runsListPromise = managedFetch(
          `${API_URL}${prefix}/simulation/intermix/${runId}/runs`,
          { headers: authHeaders },
          isSharePage ? null : currentUser
        );
        const annotationPromise = isSharePage
          ? Promise.resolve(null)
          : managedFetch(
              `${API_URL}${prefix}/note/${runId}`,
              { headers: authHeaders },
              currentUser
            );

        const [
          { response: resultResponse, data: resultData },
          detailsResponse,
          runsListResponse,
          annotationResponse,
        ] = await Promise.all([
          resultPromise,
          detailsPromise,
          runsListPromise,
          annotationPromise,
        ]);

        if (
          !resultResponse?.ok ||
          !detailsResponse.ok ||
          (!isSharePage && !annotationResponse?.ok)
        ) {
          return;
        }

        const [detailsData, runsListData, annotationData] = await Promise.all([
          detailsResponse.json(),
          runsListResponse?.ok
            ? runsListResponse.json()
            : Promise.resolve(null),
          annotationResponse
            ? annotationResponse.json()
            : Promise.resolve(null),
        ]);

        if (resultData?.created_at) {
          resultData.created_at = new Date(resultData.created_at);
        }
        const sim = detailsData?.simulations?.[0];
        if (sim) {
          sim.created_at = new Date(sim.created_at);
        }
        if (annotationData) {
          annotationData.created_at = new Date(annotationData.created_at);
        }

        if (resultData && resultData.type === "FeatureCollection") {
          setIntermixResults(resultData);
        }

        setIntermixRunsList(runsListData?.runs ?? []);
        setIntermixRunID(runId);

        if (annotationData) {
          setAnnotations(annotationData);
        }

        if (sim) {
          setIntermixRunMetadata({
            durationMinutes: sim.duration ?? 240,
            iterations: sim.iterations ?? 1,
          });
        } else {
          setIntermixRunMetadata(null);
        }
        setIsLoadingIntermixSummary(false);
      } catch (error) {
        console.error("Error fetching simulation data:", error);
        setIsLoadingIntermixSummary(false);
        setIsLoadingIntermixExtended(false);
      } finally {
        setIsLoadingIntermixExtended(false);
      }
    };

    fetchSimulationData();
  }, [
    previousRunId,
    runId,
    setIntermixResults,
    setIntermixRunID,
    setAnnotations,
    setIntermixScenarioTree,
    setIntermixScenarioEmbers,
    setIntermixScenarioSequence,
    setIsLoadingIntermixSummary,
    setIsLoadingIntermixExtended,
    setSelectedIntermixRunIndex,
    setIntermixRunMetadata,
    setIntermixRunsList,
    BEARER_TOKEN,
    prefix,
    filterQuery,
    currentUser,
    isSharePage,
  ]);

  // Fetch tree and nodes only for the selected run index (aggregate already loaded)
  useEffect(() => {
    if (!runId || !selectedIntermixRunIndex || intermixRunID !== runId) {
      return;
    }

    let cancelled = false;
    setIsLoadingIntermixExtended(true);
    const authHeaders = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${BEARER_TOKEN}`,
    };

    const fetchTreeAndNodes = async () => {
      try {
        const { response, data } = await fetchIntermixGeoJSON(
          managedFetch(
            `${API_URL}${prefix}/simulation/intermix/${runId}/${selectedIntermixRunIndex}/tree`,
            withArrowAccept({ headers: authHeaders }),
            isSharePage ? null : currentUser
          )
        );
        if (cancelled) return;
        setIntermixScenarioTree(
          response?.ok && data?.type === "FeatureCollection" ? data : null
        );
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching run tree:", error);
          setIntermixScenarioTree(null);
        }
      }

      if (cancelled) return;

      try {
        const { response, data } = await fetchIntermixGeoJSON(
          managedFetch(
            `${API_URL}${prefix}/simulation/intermix/${runId}/${selectedIntermixRunIndex}/nodes`,
            withArrowAccept({ headers: authHeaders }),
            isSharePage ? null : currentUser
          )
        );
        if (cancelled) return;
        setIntermixScenarioSequence(
          response?.ok && data?.type === "FeatureCollection" ? data : null
        );
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching run nodes:", error);
          setIntermixScenarioSequence(null);
        }
      }

      if (!cancelled) {
        setIsLoadingIntermixExtended(false);
      }
    };

    fetchTreeAndNodes();
    return () => {
      cancelled = true;
    };
  }, [
    runId,
    selectedIntermixRunIndex,
    intermixRunID,
    setIntermixScenarioTree,
    setIntermixScenarioSequence,
    setIsLoadingIntermixExtended,
    BEARER_TOKEN,
    prefix,
    currentUser,
    isSharePage,
  ]);
  return null;
}
